/**
 * Access-style control palette — what can be placed on a form.
 *
 * This is not the database. Fields stay in the left panel. Each toolbox
 * item is a UI control: Label, Text Box, Button, Tab Control, and the rest.
 * Dropping one onto a Card creates that control unbound. A Text Box may be
 * bound later; a Label stays caption-only.
 *
 * The controls are shown in three named runs — Basic, Selection, Layout — so
 * the row reads as a Material toolbar rather than twelve identical buttons.
 * The grouping is presentation: the control model is untouched, and any
 * toolbox control the model gains without a group of its own still appears,
 * at the end of the last run.
 */
import {
  CheckSquare,
  ChevronDownSquare,
  CircleDot,
  Component,
  Image,
  LayoutPanelTop,
  List,
  Minus,
  MousePointerClick,
  Square,
  TextCursorInput,
  Type,
} from "lucide-react";
import { FD } from "@/constants/testIds";
import { TOOLBOX_CONTROL_TYPES } from "@/lib/formDesigner";
import { DRAG_MIME } from "./FormDesignerCanvas";

function setDragPayload(event, payload) {
  event.dataTransfer?.setData?.(DRAG_MIME, JSON.stringify(payload));
  if (event.dataTransfer) event.dataTransfer.effectAllowed = "copy";
}

/** One icon set, one size, one weight — the toolbar reads as a single family. */
const TOOLBOX_ICONS = {
  label: Type,
  text: TextCursorInput,
  button: MousePointerClick,
  checkbox: CheckSquare,
  radio: CircleDot,
  select: ChevronDownSquare,
  listbox: List,
  tab: LayoutPanelTop,
  image: Image,
  line: Minus,
  rectangle: Square,
};

const TOOLBOX_GROUPS = [
  { id: "basic", label: "Basic", members: ["label", "text", "button"] },
  { id: "selection", label: "Selection", members: ["checkbox", "radio", "select", "listbox"] },
  { id: "layout", label: "Layout", members: ["tab", "image", "line", "rectangle"] },
];

function toolboxGroups() {
  const grouped = TOOLBOX_GROUPS.map((group) => ({
    ...group,
    types: group.members
      .map((id) => TOOLBOX_CONTROL_TYPES.find((type) => type.id === id))
      .filter(Boolean),
  }));
  const placed = new Set(grouped.flatMap((group) => group.types.map((type) => type.id)));
  const rest = TOOLBOX_CONTROL_TYPES.filter((type) => !placed.has(type.id));
  if (rest.length) grouped[grouped.length - 1].types.push(...rest);
  return grouped.filter((group) => group.types.length > 0);
}

export default function FormDesignerToolbox({ onAddControl, readOnly = false }) {
  return (
    <div className="efd__toolbox" data-testid={FD.toolbox}>
      <div className="efd__toolbox-label">
        <Component className="efd__icon" aria-hidden="true" />
        <span className="efd__toolbox-kicker">Control Toolbox</span>
      </div>
      <div className="efd__toolbox-items" data-testid={FD.toolboxItems} role="toolbar" aria-label="Form controls">
        {toolboxGroups().map((group) => (
          <div key={group.id} className="efd__toolbox-group" data-group={group.id}>
            <span className="efd__toolbox-group-title">{group.label}</span>
            <div className="efd__toolbox-group-items">
              {group.types.map((type) => {
                const Icon = TOOLBOX_ICONS[type.id] || Square;
                return (
                  <button
                    key={type.id}
                    type="button"
                    className="efd__toolbox-item"
                    data-testid={`${FD.toolboxControl}-${type.id}`}
                    data-control-type={type.id}
                    draggable={!readOnly}
                    disabled={readOnly}
                    title={`${type.label} — drag onto a card, or click to place it`}
                    onDragStart={(event) => setDragPayload(event, { kind: "control", controlType: type.id })}
                    onClick={() => onAddControl(type.id)}
                  >
                    <Icon className="efd__toolbox-glyph" aria-hidden="true" />
                    <span className="efd__toolbox-name">{type.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
